import type {
  CreateCustomFetchOpts,
  GeneratedFetch,
  HandleLocalResponseOptions,
  LocalReactiveFetchHandler,
  REJECTED_STATUS,
  RequestOpts,
  RESOLVED_STATUS,
} from "../types/api";
import { argumentParser } from "../utils/argumentParser";
import { apiRequestOpts, handleLocalResponse } from "../utils/api";
import { type FetchResultsData, useLocalFetch } from "./apiHandler";
import { type FallbackReturnType, type SafeExecutionOptions, useSafeExecution } from "./safeExecution";

type SafeFetchType = "fetch" | "mutate" | "download";
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type SafeRequestOptions<T_Data = any> = SafeExecutionOptions<T_Data> & HandleLocalResponseOptions;
type DefaultSafeRequestOptions<T_Data> = SafeRequestOptions<T_Data> & {
  fetchType?: SafeFetchType;
  customFetchOpts?: CreateCustomFetchOpts;
};
type SafeLocalFetchMethods<
  T_ReturnType,
  T_Options extends DefaultSafeRequestOptions<T_ReturnType>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  T_Args extends any[] = any[],
  T_Fallback = FallbackReturnType<T_Options, T_ReturnType>,
> = T_Options extends { fetchType: "download" }
  ? {
      safeDownload: (...args: SafeFetchParams<T_Args, T_Fallback>) => Promise<Blob>;
    }
  : T_Options extends { fetchType: "mutate" }
    ? {
        safeMutate: (...args: SafeFetchParams<T_Args, T_Fallback>) => Promise<T_Fallback>;
      }
    : {
        safeFetch: (...args: SafeFetchParams<T_Args, T_Fallback>) => Promise<T_Fallback>;
      };

type OverrideSafeRequestOptions<T_Fallback> = Omit<SafeRequestOptions<T_Fallback>, "fallback" | "fetchType"> & {
  requestOptions?: RequestOpts;
};
type SafeFetchParams<T_Args extends unknown[], T_Fallback> = {
  [k in keyof T_Args]: T_Args[k] extends RequestOpts | undefined ? OverrideSafeRequestOptions<T_Fallback> : T_Args[k];
};

function extractSafeOptions<
  T_FetchMethod extends GeneratedFetch,
  T_ReturnType extends FetchResultsData<T_FetchMethod, RESOLVED_STATUS>,
  T_Args extends Parameters<T_FetchMethod>,
  T_Options extends SafeRequestOptions<T_ReturnType>,
>(fn: T_FetchMethod, args: unknown[]) {
  // TODO: this method of knowing the expected arguments is not ideal, we should use a more robust way to extract the function signature
  // it fails if the function is mocked on the test, so we need to use the original function
  const expectedArguments = argumentParser(fn);
  // we make sure to get the last parameter as the options parameter, even if is undefined
  const optionsParams = args[expectedArguments.length - 1];
  const otherParams = args.slice(0, expectedArguments.length - 1);
  const { requestOptions, ...safeOptions } = (optionsParams ?? {}) as OverrideSafeRequestOptions<T_Options>;

  // if requestOptions is not defined, we use the default apiRequestOpts
  const sanitizedFetchArgs = [
    ...$ck.utils.sanitizeRequestPayload(otherParams),
    requestOptions ?? apiRequestOpts({}),
  ] as T_Args;

  return {
    sanitizedFetchArgs,
    safeOptions: safeOptions as T_Options,
  };
}

export function useSafeLocalFetch<
  T_FetchMethod extends GeneratedFetch,
  T_ReturnType extends FetchResultsData<T_FetchMethod, RESOLVED_STATUS>,
  T_Args extends Parameters<T_FetchMethod>,
  T_FetchTypeError extends FetchResultsData<T_FetchMethod, REJECTED_STATUS>,
  T_DefaultOptions extends DefaultSafeRequestOptions<T_ReturnType>,
>(
  idName: string,
  fetchMethod: T_FetchMethod,
  defaultOptions?: T_DefaultOptions,
): SafeLocalFetchMethods<T_ReturnType, T_DefaultOptions, T_Args> &
  // TODO: use LocalReactiveFetchHandler<FallbackReturnType<T_DefaultOptions, T_ReturnType>... and return data as the fallback
  LocalReactiveFetchHandler<T_ReturnType, T_FetchTypeError, T_Args> {
  const { fetchType = "fetch", customFetchOpts, ...defaultSafeOptions } = defaultOptions ?? ({} as T_DefaultOptions);

  const { handleSafeExecution } = useSafeExecution();
  const { fetch, mutate, ...rest } = useLocalFetch(idName, fetchMethod, customFetchOpts);

  const safeCall = (...args: SafeFetchParams<T_Args, FallbackReturnType<T_DefaultOptions, T_ReturnType>>) => {
    const { sanitizedFetchArgs, safeOptions } = extractSafeOptions(fetchMethod, args);

    const mergedOptions = {
      ...defaultSafeOptions,
      ...safeOptions,
      executionName: safeOptions.executionName ?? defaultSafeOptions.executionName ?? `${fetchType} ${idName}`,
    };

    return handleSafeExecution(async () => {
      const method = fetchType === "mutate" ? mutate : fetch;
      const response = await method(...sanitizedFetchArgs);
      const { data, blob } = handleLocalResponse<T_ReturnType, T_FetchTypeError>(response, mergedOptions);

      return fetchType === "download" ? blob : data;
    }, mergedOptions);
  };
  const methodName = fetchType === "download" ? "safeDownload" : fetchType === "mutate" ? "safeMutate" : "safeFetch";
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return { ...rest, [methodName]: safeCall } as any;
}
